import type { components } from '@trackercop/contracts';
import type { LineageNode } from '@/lib/lineage-graph';
import { Badge } from '@/components/ui/badge';
import { ScriptCategoryChip } from '@/components/category-chip';

type DetectedScript = components['schemas']['DetectedScript'];

interface LineageSidePanelProps {
  node: LineageNode | null;
  allScripts: readonly DetectedScript[];
}

// Shows the scripts a selected domain is responsible for. Each row
// includes the script's URL, category, VPPA/before-consent marker, and
// the classification source. The panel renders plain-text children
// only — user-controlled strings (script URLs, domains) are never
// injected via dangerouslySetInnerHTML.
export function LineageSidePanel({ node, allScripts }: LineageSidePanelProps) {
  if (!node) {
    return (
      <div className="rounded-md border bg-background p-3 text-xs text-muted-foreground" data-testid="lineage-panel-empty">
        Click a node in the graph to see the scripts it represents.
      </div>
    );
  }

  const scripts = allScripts.filter((s) => s.domain === node.domain);

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3" data-testid="lineage-panel">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-semibold">{node.domain}</span>
        <ScriptCategoryChip category={node.category} />
      </div>
      <div className="text-[11px] text-muted-foreground">
        {scripts.length} script{scripts.length === 1 ? '' : 's'} from this domain
      </div>
      <ul className="flex max-h-80 flex-col gap-1 overflow-auto text-xs">
        {scripts.map((s) => (
          <li key={s.id} className="flex flex-col gap-0.5 rounded-sm border bg-muted/30 p-1.5">
            <div className="flex items-center gap-1">
              {s.loadedBeforeConsent ? (
                <Badge variant="destructive">Before</Badge>
              ) : (
                <Badge variant="muted">After</Badge>
              )}
              <ScriptCategoryChip category={s.category} />
              {s.consentMode && (
                <span className="text-[10px] text-muted-foreground">{s.consentMode}</span>
              )}
            </div>
            <span className="break-all font-mono text-[10px] text-muted-foreground">{s.url}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
